import { useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { ComboboxPrimitive } from '@worldcoo/ui/components/combobox';
import { CheckIcon, ChevronDown, Loader2, SearchIcon } from 'lucide-react';
import { Button } from '@worldcoo/ui/components/button';
import { Popover, PopoverContent, PopoverTrigger } from '@worldcoo/ui/components/popover';
import { cn } from '@worldcoo/ui/lib/utils';

type Option = { value: string; label: string };

// ── Shared pill trigger ──────────────────────────────────────────────────────

function pillClassName(active: boolean, className?: string) {
	return cn(
		'border-border bg-background hover:bg-muted inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-xl border px-4 text-sm font-medium transition-colors cursor-pointer',
		active && 'border-primary bg-primary/5 text-foreground',
		className,
	);
}

function FilterPillLabel(props: { label: string; summary?: string | number; isLoading?: boolean }) {
	return (
		<>
			<span>{props.label}</span>
			{props.summary != null && props.summary !== '' && (
				<span className="text-muted-foreground text-xs font-normal">({props.summary})</span>
			)}
			{props.isLoading ? (
				<Loader2 className="text-muted-foreground size-4 animate-spin" />
			) : (
				<ChevronDown className="text-muted-foreground size-4" />
			)}
		</>
	);
}

// ── Date range ───────────────────────────────────────────────────────────────

export function DateRangeFilter(props: {
	dateFrom: string | undefined;
	dateTo: string | undefined;
	onChange: (next: { dateFrom?: string; dateTo?: string }) => void;
	isLoading?: boolean;
}) {
	const { t } = useLingui();
	const [open, setOpen] = useState(false);
	const [fromDraft, setFromDraft] = useState(props.dateFrom ?? '');
	const [toDraft, setToDraft] = useState(props.dateTo ?? '');
	const active = !!(props.dateFrom || props.dateTo);
	const summary = active ? [props.dateFrom, props.dateTo].filter(Boolean).join(' → ') : undefined;

	const handleOpenChange = (next: boolean) => {
		if (next) {
			setFromDraft(props.dateFrom ?? '');
			setToDraft(props.dateTo ?? '');
		}
		setOpen(next);
	};

	const apply = () => {
		props.onChange({ dateFrom: fromDraft || undefined, dateTo: toDraft || undefined });
		setOpen(false);
	};

	const clear = () => {
		setFromDraft('');
		setToDraft('');
		props.onChange({ dateFrom: undefined, dateTo: undefined });
		setOpen(false);
	};

	return (
		<Popover open={open} onOpenChange={handleOpenChange}>
			<PopoverTrigger className={pillClassName(active)}>
				<FilterPillLabel label={t`Date`} summary={summary} isLoading={props.isLoading} />
			</PopoverTrigger>
			<PopoverContent align="start" className="w-80">
				<div className="flex flex-col gap-3">
					<div className="flex flex-col gap-1.5">
						<label className="text-muted-foreground text-xs font-medium" htmlFor="dashboard-date-from">
							{t`From`}
						</label>
						<input
							id="dashboard-date-from"
							type="date"
							value={fromDraft}
							onChange={(e) => setFromDraft(e.target.value)}
							max={toDraft || undefined}
							className="border-border bg-background focus-visible:border-ring h-9 rounded-md border px-3 text-sm outline-none"
						/>
					</div>
					<div className="flex flex-col gap-1.5">
						<label className="text-muted-foreground text-xs font-medium" htmlFor="dashboard-date-to">
							{t`To`}
						</label>
						<input
							id="dashboard-date-to"
							type="date"
							value={toDraft}
							onChange={(e) => setToDraft(e.target.value)}
							min={fromDraft || undefined}
							className="border-border bg-background focus-visible:border-ring h-9 rounded-md border px-3 text-sm outline-none"
						/>
					</div>
					<div className="flex items-center justify-between pt-1">
						<Button variant="ghost" size="sm" onClick={clear} disabled={!active && !fromDraft && !toDraft}>
							{t`Clear`}
						</Button>
						<Button size="sm" onClick={apply}>
							{t`Apply`}
						</Button>
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
}

// ── Single-select combobox (merchant, city) ──────────────────────────────────

export function SingleComboboxFilter(props: {
	label: string;
	searchPlaceholder: string;
	emptyMessage: string;
	allLabel: string;
	options: Option[];
	value: string | undefined;
	onChange: (id: string | undefined) => void;
	isLoading?: boolean;
}) {
	const selected = props.options.find((o) => o.value === props.value);

	return (
		<ComboboxPrimitive.Root
			items={props.options}
			value={selected ?? null}
			onValueChange={(next: Option | null) => props.onChange(next?.value)}
			isItemEqualToValue={(a: Option, b: Option) => a.value === b.value}
		>
			<ComboboxPrimitive.Trigger className={pillClassName(!!selected)}>
				<FilterPillLabel label={props.label} summary={selected?.label} isLoading={props.isLoading} />
			</ComboboxPrimitive.Trigger>
			<FilterComboboxPopup
				searchPlaceholder={props.searchPlaceholder}
				emptyMessage={props.emptyMessage}
				hasOptions={props.options.length > 0}
				header={
					<button
						type="button"
						onClick={() => props.onChange(undefined)}
						className="text-muted-foreground hover:text-foreground flex w-full items-center justify-between px-2 py-1.5 text-sm"
					>
						{props.allLabel}
						{!selected && <CheckIcon className="size-4" />}
					</button>
				}
			>
				{(item: Option) => (
					<ComboboxPrimitive.Item value={item} className={itemClassName}>
						<span className="truncate">{item.label}</span>
						<ComboboxPrimitive.ItemIndicator className="ml-auto">
							<CheckIcon className="size-4" />
						</ComboboxPrimitive.ItemIndicator>
					</ComboboxPrimitive.Item>
				)}
			</FilterComboboxPopup>
		</ComboboxPrimitive.Root>
	);
}

// ── Campaign status filter (active / paused / funded) ───────────────────────
// "Draft" and not-yet-started ("notFundraising") campaigns never reach the
// results table — it only lists campaigns that are raising or have raised (see
// `getNgoDashboardCampaigns`), so we don't expose those as filter options here.

export type CampaignStatusFilterValue = 'active' | 'notFundraising' | 'paused' | 'funded';

export function StatusFilter(props: {
	value: CampaignStatusFilterValue | undefined;
	onChange: (next: CampaignStatusFilterValue | undefined) => void;
	isLoading?: boolean;
}) {
	const { t } = useLingui();
	const [open, setOpen] = useState(false);
	const options: { value: CampaignStatusFilterValue; label: string }[] = [
		{ value: 'active', label: t`Active` },
		{ value: 'funded', label: t`Funded` },
		{ value: 'paused', label: t`Paused` },
	];
	const selected = options.find((o) => o.value === props.value);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger className={pillClassName(!!selected)}>
				<FilterPillLabel label={t`Status`} summary={selected?.label} isLoading={props.isLoading} />
			</PopoverTrigger>
			<PopoverContent align="start" className="w-48 p-1">
				<button
					type="button"
					onClick={() => {
						props.onChange(undefined);
						setOpen(false);
					}}
					className="text-muted-foreground hover:bg-muted hover:text-foreground flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm"
				>
					{t`All statuses`}
					{!selected && <CheckIcon className="size-4" />}
				</button>
				{options.map((option) => (
					<button
						key={option.value}
						type="button"
						onClick={() => {
							props.onChange(option.value);
							setOpen(false);
						}}
						className="text-foreground hover:bg-muted flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm"
					>
						{option.label}
						{selected?.value === option.value && <CheckIcon className="size-4" />}
					</button>
				))}
			</PopoverContent>
		</Popover>
	);
}

// ── Combobox popup ───────────────────────────────────────────────────────────

const itemClassName =
	'data-highlighted:bg-accent data-highlighted:text-accent-foreground flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-hidden select-none group/combobox-item';

function FilterComboboxPopup(props: {
	searchPlaceholder: string;
	emptyMessage: string;
	hasOptions: boolean;
	header?: React.ReactNode;
	footer?: React.ReactNode;
	children: React.ReactNode | ((item: Option) => React.ReactNode);
}) {
	return (
		<ComboboxPrimitive.Portal>
			<ComboboxPrimitive.Positioner sideOffset={6} align="start" className="isolate z-50">
				<ComboboxPrimitive.Popup className="bg-popover text-popover-foreground ring-foreground/10 data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 flex w-64 max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-lg shadow-md ring-1 duration-100">
					{props.hasOptions && (
						<div className="border-border flex items-center gap-2 border-b px-2 py-1.5">
							<SearchIcon className="text-muted-foreground size-4 shrink-0" />
							<ComboboxPrimitive.Input
								placeholder={props.searchPlaceholder}
								className="placeholder:text-muted-foreground flex-1 bg-transparent text-sm outline-none"
							/>
						</div>
					)}
					{props.header && (
						<div className={cn('p-1', props.hasOptions && 'border-border border-b')}>{props.header}</div>
					)}
					{props.hasOptions && (
						<>
							<ComboboxPrimitive.List className="max-h-64 overflow-y-auto overscroll-contain p-1">
								{props.children as never}
							</ComboboxPrimitive.List>
							<ComboboxPrimitive.Empty className="text-muted-foreground hidden justify-center py-4 text-center text-sm group-data-empty/combobox-content:flex">
								{props.emptyMessage}
							</ComboboxPrimitive.Empty>
						</>
					)}
					{props.footer && <div className="border-border border-t p-1">{props.footer}</div>}
				</ComboboxPrimitive.Popup>
			</ComboboxPrimitive.Positioner>
		</ComboboxPrimitive.Portal>
	);
}
